import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useRenameFile } from '@/api/files';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  file: { id: string; name: string; folder_id: string };
}

export function RenameFileModal({ open, onClose, file }: Props) {
  const [name, setName] = useState(file.name);
  const rename = useRenameFile();
  const toast = useToast();

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === file.name) return;
    rename.mutate(
      { id: file.id, name: trimmed },
      {
        onSuccess: () => {
          toast.success('File renamed');
          onClose();
        },
        onError: (e) => toast.error('Rename failed', apiError(e)),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rename file"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={rename.isPending}
            disabled={!name.trim() || name.trim() === file.name}
          >
            Rename
          </Button>
        </>
      }
    >
      <p className="text-xs text-slate-500 mb-3">
        Renaming a file doesn't affect its versions, comments, or audit history.
      </p>
      <label className="block text-sm font-medium text-slate-700 mb-1">File name</label>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
        }}
        autoFocus
      />
    </Modal>
  );
}
